import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Allergen, IngredientDto } from '@fewerbags/schema';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { api, queryKeys } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { allergenRows, toggleAllergenVote, withAllergenVote } from '../../lib/ingredientDisplay';

/**
 * D31: the allergens this household's planner uses for an ingredient, and a
 * toggle per allergen. A correction applies to this household at once and to
 * everyone once another household agrees; other households' votes are never
 * shown.
 */
export function AllergenCorrections({ ingredient }: { ingredient: IngredientDto }) {
  const qc = useQueryClient();
  const key = queryKeys.ingredientDetail(ingredient.id);
  const [editing, setEditing] = useState(false);
  const rows = allergenRows(ingredient);
  const contains = rows.filter((r) => r.effective);
  const clearedByUs = rows.filter((r) => r.shared && !r.effective);

  const vote = useMutation({
    mutationFn: (v: { allergen: Allergen; present: boolean | null }) =>
      api.ingredients.voteAllergens(ingredient.id, { votes: [v] }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<IngredientDto>(key);
      if (previous) qc.setQueryData(key, withAllergenVote(previous, v));
      return { previous };
    },
    onError: (_err, _v, context) => {
      if (context?.previous) qc.setQueryData(key, context.previous);
    },
    onSuccess: (dto) => qc.setQueryData(key, dto),
  });

  return (
    <div className="flex flex-col gap-3">
      {contains.length > 0 ? (
        <ul className="flex flex-wrap gap-2" aria-label="Contains">
          {contains.map((r) => (
            <li
              key={r.allergen}
              className="rounded-full border border-line bg-brand-soft px-3 py-1 text-sm"
            >
              {r.label}
              {r.corrected && !r.shared ? (
                <span className="ml-1 text-xs text-muted">· your correction</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted">
          No allergens detected. That is not a promise it is safe.
        </p>
      )}
      {clearedByUs.length > 0 ? (
        <p className="text-sm text-muted">
          You marked it as not containing {clearedByUs.map((r) => r.label.toLowerCase()).join(', ')}
          .
        </p>
      ) : null}

      {editing ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-muted">
            Tap to correct it for your household. Other households see the change once another
            household agrees.
          </p>
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {rows.map((r) => (
              <li key={r.allergen}>
                <button
                  type="button"
                  aria-pressed={r.effective}
                  disabled={vote.isPending}
                  onClick={() => vote.mutate(toggleAllergenVote(r))}
                  className={`flex min-h-11 w-full items-center justify-between rounded-xl border px-3 py-2 text-left text-sm ${
                    r.effective ? 'border-brand bg-brand-soft text-ink' : 'border-line text-muted'
                  }`}
                >
                  <span>
                    {r.effective ? 'Contains ' : 'No '}
                    {r.label.toLowerCase()}
                  </span>
                  <span className="text-xs text-muted">
                    {r.corrected ? 'your correction' : 'shared'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {vote.isError ? <Alert tone="error">{messageForError(vote.error)}</Alert> : null}
        </div>
      ) : null}
      <div>
        <Button variant="secondary" size="sm" block={false} onClick={() => setEditing((e) => !e)}>
          {editing ? 'Done' : 'Edit'}
        </Button>
      </div>
    </div>
  );
}
